import { useEffect, useState, type ReactNode } from 'react';
import { clearSession, type SignoutReason } from './api/client.js';
import { getMe, logout } from './api/endpoints.js';
import { useApi } from './api/hooks.js';
import { useSignedIn, useSignoutReason } from './api/session-hooks.js';
import { homeHash } from './flow/invite-link.js';
import { BLANK_FLAGS, clearFlags, readFlags, resumeStep } from './flow/machine.js';
import { externalReturn, RETURN_COPY, type ExternalReturn } from './flow/return.js';
import { ScreenBoundary } from './ui/boundary.js';
import { BrandPulse, FlowScreen, FlowSub, FlowTitle, PrimaryButton } from './ui/flow.js';
import { Failure } from './ui/kit.js';
import { AuthScreen } from './screens/flow/auth.js';
import { OnboardingFlow } from './screens/flow/index.js';
import { DashboardScreen } from './screens/dashboard.js';
import { ProfileScreen } from './screens/profile.js';
import { QuizScreen } from './screens/quiz.js';
import { ScoreHistoryScreen } from './screens/score.js';
import { PlansScreen } from './screens/plans.js';
import { ConsentScreen } from './screens/consent.js';
import { EnrolmentScreen } from './screens/enrolment.js';
import { HouseholdScreen } from './screens/household.js';
import { MemberScreen } from './screens/member.js';
import { InvitesScreen } from './screens/invites.js';
import { PhotosScreen } from './screens/photos.js';
import { ClustersScreen } from './screens/clusters.js';
import { ReportsScreen } from './screens/reports.js';
import { DevicesScreen } from './screens/devices.js';
import { AccountScreen } from './screens/account.js';
import { StatusScreen } from './screens/status.js';

/**
 * The journey-first shell, reshaped to the mobile app's flow
 * (onboarding-flow-and-api.md): sign in with phone + OTP, walk the guided
 * onboarding at #/setup/<step>, land on the Dashboard. The management screens
 * survive as tabs BEHIND the dashboard — same wiring, same honest rendering —
 * instead of being the whole app.
 */

interface ShellRoute {
  id: string;
  label: string;
  /** Which primary tab this route lives under; the subnav groups by it. */
  tab: string;
  /** Shown in the header bar. */
  primary?: boolean;
  /** Reachable without a session — the status screen stays public. */
  publicRoute?: boolean;
  /**
   * A route whose path carries a trailing identifier (`member/<personId>`).
   * It matches by PREFIX and is deliberately absent from the subnav: there is
   * one of these per person, so a list of them is the roster, not a nav bar.
   */
  param?: boolean;
  render: (param: string) => ReactNode;
}

const ROUTES: ShellRoute[] = [
  { id: 'home', label: 'Dashboard', tab: 'home', primary: true, render: () => <DashboardScreen /> },
  { id: 'photos', label: 'Photos', tab: 'photos', primary: true, render: () => <PhotosScreen /> },
  { id: 'reports', label: 'Reports', tab: 'reports', primary: true, render: () => <ReportsScreen /> },
  { id: 'family', label: 'Family', tab: 'family', primary: true, render: () => <HouseholdScreen /> },
  { id: 'invites', label: 'My invites', tab: 'family', render: () => <InvitesScreen /> },
  {
    id: 'member',
    label: 'Family member',
    tab: 'family',
    param: true,
    render: (personId) => <MemberScreen personId={personId} />,
  },
  { id: 'clusters', label: 'Discovered clusters', tab: 'family', render: () => <ClustersScreen /> },
  { id: 'account', label: 'Account', tab: 'account', primary: true, render: () => <ProfileScreen /> },
  { id: 'quiz', label: 'Quiz & score', tab: 'account', render: () => <QuizScreen /> },
  { id: 'score', label: 'Score history', tab: 'account', render: () => <ScoreHistoryScreen /> },
  { id: 'plans', label: 'Plans & billing', tab: 'account', render: () => <PlansScreen /> },
  { id: 'consent', label: 'Consent', tab: 'account', render: () => <ConsentScreen /> },
  { id: 'enrolment', label: 'Enrolment', tab: 'account', render: () => <EnrolmentScreen /> },
  { id: 'devices', label: 'Devices & sessions', tab: 'account', render: () => <DevicesScreen /> },
  { id: 'delete', label: 'Delete account', tab: 'account', render: () => <AccountScreen /> },
  {
    id: 'status',
    label: 'Environment status',
    tab: 'account',
    publicRoute: true,
    render: () => <StatusScreen />,
  },
];

function currentPath(): string {
  return window.location.hash.replace(/^#\/?/, '');
}

function useHashPath(): string {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const onChange = () => setPath(currentPath());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return path;
}

/**
 * The involuntary-signout notice. Rendered ONLY when the session ended without
 * the user asking (clearSession carried a reason): before this, a revoked or
 * refused session bounced to the phone screen with no explanation, and the
 * "why" existed only in the api's logs. The server's own message renders
 * verbatim below the headline — this client never invents data.
 */
/**
 * What a provider-return tab shows instead of the sign-in screen. Sessionless
 * by design: "close this tab" needs no authentication, and the rare same-tab
 * return (a popup blocker forced the flow into this tab) gets a Continue
 * button that rejoins the wizard, whose resume logic reads the fresh state.
 */
function ExternalReturnScreen({ kind, signedIn }: { kind: ExternalReturn; signedIn: boolean }) {
  const copy = RETURN_COPY[kind];
  return (
    <div className="flow-stage">
      <FlowScreen>
        <div className="flow-center">
          <span className={copy.failed === true ? 'return-badge failed' : 'return-badge'} aria-hidden="true">
            {copy.failed === true ? '✕' : '✓'}
          </span>
          <FlowTitle>{copy.title}</FlowTitle>
          <FlowSub>{copy.detail}</FlowSub>
          {signedIn ? (
            <PrimaryButton
              onClick={() => {
                window.history.replaceState(null, '', '/#/setup');
                window.dispatchEvent(new HashChangeEvent('hashchange'));
              }}
            >
              Continue here
            </PrimaryButton>
          ) : (
            <a className="flow-link" href="/">
              Open the app here instead
            </a>
          )}
        </div>
      </FlowScreen>
    </div>
  );
}

function SignoutNotice({ reason }: { reason: SignoutReason }) {
  const headline =
    {
      TOKEN_REUSED:
        'You were signed out for safety: this session was used from somewhere else.',
      ACCOUNT_DELETING: 'You were signed out because this account is being deleted.',
      FORBIDDEN: 'You were signed out because this account is no longer active.',
    }[reason.code] ?? 'You were signed out.';
  return (
    <div className="signout-notice" role="alert">
      <strong>{headline}</strong>
      <span>{reason.message}</span>
    </div>
  );
}

function replaceHash(hash: string): void {
  window.history.replaceState(null, '', hash);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

/**
 * The Splash: signed in with no destination. One read of /v1/me decides between
 * resuming onboarding and the dashboard — the same table the mobile splash
 * routes by, minus the local checkpoint cases the wizard resolves itself.
 */
function Gate() {
  const me = useApi(getMe, []);

  useEffect(() => {
    if (me.data === null) return;
    const hasExtras =
      me.data.person.employer_name !== null || me.data.person.school_name !== null;
    const target = resumeStep(me.data.onboarding, hasExtras, readFlags());
    replaceHash(target === 'done' ? homeHash() : '#/setup');
  }, [me.data]);

  return (
    <FlowScreen>
      {me.error !== null ? (
        <>
          <Failure error={me.error} />
          <PrimaryButton onClick={me.reload}>Try again</PrimaryButton>
        </>
      ) : (
        <div className="flow-center">
          <BrandPulse />
        </div>
      )}
    </FlowScreen>
  );
}

export function App() {
  const path = useHashPath();
  const signedIn = useSignedIn();
  const signoutReason = useSignoutReason();

  // A tab coming back from Stripe or DocuSeal. Checked before ANY auth
  // branching, because this tab usually has no session (sessionStorage is
  // per-tab) and must not render the sign-in screen — that reads as "paying
  // signed me out". The original tab owns the flow and advances by itself.
  const returned = externalReturn(window.location.pathname);
  if (returned !== null) {
    return <ExternalReturnScreen kind={returned} signedIn={signedIn} />;
  }

  // Signed out: the flow starts at the phone number; only status stays public.
  if (!signedIn && path !== 'status') {
    return (
      <div className="flow-stage">
        {signoutReason !== null && <SignoutNotice reason={signoutReason} />}
        <ScreenBoundary screen="auth">
          <AuthScreen
            onVerified={(onboarding) => {
              // CLEARED ON EVERY LOGIN, and that is what makes the remaining
              // local flags safe. The blob is not keyed to an account, so
              // without this the next person to sign in on this browser
              // inherits the last one's `purpose` and `familyComplete`. It used
              // to inherit their skips too — those are server state now, per
              // person, which is the half that actually mattered.
              //
              // Nothing is lost by clearing: progress comes from `onboarding`,
              // and a returning user resumes where the server says rather than
              // where this browser last remembered.
              clearFlags();
              // hasProfileExtras: false is unknowable here — verify answers with
              // tokens and `onboarding`, no person fields — and it is harmless,
              // because the only decision this makes is home vs setup, and that
              // turns on `next_step === null`, which ignores it. The wizard
              // recomputes with the real value from /v1/me on arrival.
              const target = resumeStep(onboarding, false, BLANK_FLAGS);
              replaceHash(target === 'done' ? homeHash() : '#/setup');
            }}
          />
        </ScreenBoundary>
      </div>
    );
  }

  // The wizard is full-bleed: no shell chrome, exactly like the mobile stack.
  if (signedIn && (path === 'setup' || path.startsWith('setup/'))) {
    const stepId = path.startsWith('setup/') ? path.slice('setup/'.length) : '';
    return (
      <div className="flow-stage">
        <ScreenBoundary screen={`setup-${stepId}`}>
          <OnboardingFlow stepId={stepId} />
        </ScreenBoundary>
      </div>
    );
  }

  if (signedIn && path === '') {
    return (
      <div className="flow-stage">
        <Gate />
      </div>
    );
  }

  /**
   * Exact first, then prefix. Exact-first matters: without it a future route
   * literally named `member` would be shadowed by the parametrised one and
   * render with an empty id.
   */
  const exact = ROUTES.find((candidate) => candidate.id === path);
  const prefixed = ROUTES.find(
    (candidate) => candidate.param === true && path.startsWith(`${candidate.id}/`),
  );
  const route = exact ?? prefixed ?? ROUTES[0]!;
  const param = prefixed !== undefined && exact === undefined ? path.slice(route.id.length + 1) : '';
  const primaries = ROUTES.filter((entry) => entry.primary === true);
  // Parametrised routes stay out of the subnav — one per person is a roster.
  const siblings = ROUTES.filter((entry) => entry.tab === route.tab && entry.param !== true);

  return (
    <div className="shell">
      <header className="topbar">
        <a className="brand" href="#/home">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 1.8 13 4v4.5c0 2.9-2 4.5-5 5.5-3-1-5-2.6-5-5.5V4z" fill="#380E99" />
          </svg>
          ImageShield
        </a>
        <nav className="topnav">
          {primaries.map((entry) => (
            <a
              key={entry.id}
              href={`#/${entry.id}`}
              aria-current={entry.tab === route.tab}
              className="topnav-link"
            >
              {entry.label}
            </a>
          ))}
        </nav>
        {signedIn && (
          <button
            type="button"
            className="signout"
            onClick={() => {
              // Best-effort: the session dies locally even if the api call
              // fails, and the next user starts a clean flow.
              logout().catch(() => undefined);
              clearFlags();
              clearSession();
              replaceHash('#/');
            }}
          >
            Sign out
          </button>
        )}
      </header>
      {siblings.length > 1 && (
        <nav className="subnav">
          {siblings.map((entry) => (
            <a
              key={entry.id}
              href={`#/${entry.id}`}
              aria-current={entry.id === route.id}
              className="subnav-link"
            >
              {entry.label}
            </a>
          ))}
        </nav>
      )}
      <main className="main">
        {/* Keyed on the screen so navigating away resets a boundary that caught. */}
        <ScreenBoundary screen={route.id}>{route.render(param)}</ScreenBoundary>
      </main>
    </div>
  );
}
